import { getDateNow } from '@dwellet/dwellet-utils'
import arrayMove from 'array-move'
import { toast } from 'react-toastify'
import { SubmissionError } from 'redux-form'
import { SagaIterator } from 'redux-saga'
import { all, call, select, put, fork } from 'redux-saga/effects'
import {
  initializeBeforeLoginApartmentSuccess,
  saveBeforeLoginApartmentInfoSuccess,
  updateApartmentInformationFail,
  saveBeforeLoginApartmentInfoFail,
  addBeforeLoginApartmentMediaSuccess,
  addBeforeLoginApartmentMediaFail,
  deleteBeforeLoginApartmentMediaSuccess,
  deleteBeforeLoginApartmentMediaFail,
  reorderBeforeLoginApartmentMediaSuccess,
  reorderBeforeLoginApartmentMediaFail,
  confirmBeforeLoginApartmentMediaSuccess,
  confirmBeforeLoginApartmentMediaFail,
  confirmBeforeLoginApartmentSuccess,
  confirmBeforeLoginApartmentFail,
  updateApartmentClientInformationSuccess,
  addMissingClientInfoSuccess,
  addMissingClientInfoFail,
  addMissingClientInfoToOverviewSuccess,
  addMissingClientInfoToOverviewFail,
  signUpBeforeLoginSuccess
} from '../../actions/apartments'
import { openDialog, closeDialog } from '../../actions/dialogs'
import { navigate } from '../../actions/routes'
import { addFormSubmit, addStepCompletion } from '../../config/googleTagManager'
import { showParking } from '../../containers/Apartment/Forms/ApartmentConfirmationForm'
import { promiseAllHelper } from '../../libs/async'
import {
  selectBeforeLoginApartment,
  selectBeforeLoginMedia,
  selectApartmentsWithoutClientInfoByUserId
} from '../../selectors/apartments'
import { selectUserId, selectUserIsAuthenticated } from '../../selectors/users'
import Api, { PreSignedS3URLResponse } from '../../services/apartments'
import { uploadFileUsingPreSignedURL } from '../../services/storage/s3'
import i18n from '../../translations/i18n'
import {
  BeforeLoginApartment,
  ApartmentInformation,
  ApartmentConfirmation,
  AddBeforeLoginApartment,
  ApartmentClientInformation,
  Apartment
} from '../../types/apartments'
import {
  ImageFileWithObjectURL,
  IndexedFilesAndMetadata
} from '../../types/files'
import {
  HandleUpdateApartmentInfoParameters,
  HandleUpdateApartmentClientParameters
} from '../../types/formData'
import { BeforeLoginApartmentConfirmationForm } from '../../types/forms/createApartment'
import { StepCompletionEventNames, ListingCreationSteps } from '../../types/GTM'
import {
  getInitialBeforeLoginApartment,
  generateApartmentAddress,
  hasOtherOwnerDataIfNeeded
} from '../../utils/apartments'
import {
  imageFilesWithObjectURLToFiles,
  getFilesMetadataAndIndex
} from '../../utils/files'
import { validateHasTooManyImages } from '../../utils/validation/apartments'
import { uploadFile } from '../apartments'
import {
  deleteUnneededApartmentInfo,
  deleteUnneededConfirmData,
  deleteUnneededClientInfo
} from '../helpers/apartments'
import { handleSignupOnApartmentConfirmation } from '../users'
import { updateApartmentMediaURLs } from '../../services/apartments';
import { validateEmail } from '../../services/users'

export function* handleInitializeBeforeLoginApartment(): SagaIterator {
  let beforeLoginApartment = yield select(selectBeforeLoginApartment)
  if (!beforeLoginApartment) {
    beforeLoginApartment = getInitialBeforeLoginApartment()
    addStepCompletion(
      StepCompletionEventNames.listingCreation,
      '0_entered_funnel'
    )
  }
  yield put(initializeBeforeLoginApartmentSuccess(beforeLoginApartment))
}

export function* handleSaveBeforeLoginApartmentInfo({
  payload: { formData, resolve, reject }
}: HandleUpdateApartmentInfoParameters): SagaIterator {
  try {
    let beforeLoginApartmentWithoutNewInfo:
      | BeforeLoginApartment
      | undefined = yield select(selectBeforeLoginApartment)
    if (!beforeLoginApartmentWithoutNewInfo) {
      yield call(handleInitializeBeforeLoginApartment)
      beforeLoginApartmentWithoutNewInfo = yield select(
        selectBeforeLoginApartment
      )
      if (!beforeLoginApartmentWithoutNewInfo) {
        throw new Error(
          'Apartment missing from state and could not initialize beforeLoginApartment'
        )
      }
    }
    const {
      availableFromDate,
      price,
      waterFeeBasis,
      waterFee,
      ...reqRawData
    } = formData
    if (reqRawData.notes == undefined) reqRawData.notes = ''
    const switchDataObj = {
      availableFromDate,
      price: parseInt(price).toString(),
      waterFeeBasis,
      waterFee
    }
    waterFeeBasis === 'included' && delete switchDataObj.waterFee
    // is confirmed handling needed here so user can't edit confirmed apartment?

    // const isAlreadyConfirmed = metadata.confirmed
    // if (isAlreadyConfirmed) {
    //   yield put(navigate(`/users/${userId}/apartments/${apartmentId}/steps/2`))
    //   return
    // }

    const streetAddress =
      typeof formData?.autocompleteLocation === 'string'
        ? formData?.autocompleteLocation
        : formData?.autocompleteLocation?.label

    const formInformation: ApartmentInformation = {
      ...reqRawData,
      streetAddress,
      address: generateApartmentAddress({
        streetAddress,
        streetNumber: formData?.streetNumber,
        // UI IS MISSING SUCH FIELDS, NO NEED THAT FOR NOW
        buildingLetter: formData.buildingLetter,
        apartmentNumber: formData.apartmentNumber
      })
    }

    const information = deleteUnneededApartmentInfo(formInformation)
    const reqInformation: ApartmentInformation = {
      ...information,
      heating: 'electric',
      parking: 'no',
      petsAllowed: 'no',
      sauna: 'no',
      storage: 'no'
    }
    const validatedInfoForm: ApartmentInformation = yield call(
      Api.validateApartmentForm,
      'information',
      reqInformation
    )
    if (!validatedInfoForm) {
      throw new Error('Missing validated info form')
    }
    const beforeLoginApartment: BeforeLoginApartment = {
      ...beforeLoginApartmentWithoutNewInfo,
      information: validatedInfoForm,
      metadata: {
        ...beforeLoginApartmentWithoutNewInfo?.metadata,
        information: true
      },
      switchedData: {
        ...switchDataObj
      },
      updatedAt: getDateNow()
    }
    yield put(saveBeforeLoginApartmentInfoSuccess(beforeLoginApartment))
    resolve()
    yield put(navigate(`/create/apartment/steps/9`))
    // // don't send GTM data for admin actions
    // if (!isAlreadyConfirmed) {
    addFormSubmit('apartmentInfo')
    addStepCompletion(
      StepCompletionEventNames.listingCreation,
      ListingCreationSteps.filledListingDetails
    )
    // }
  } catch (error) {
    yield put(updateApartmentInformationFail(error.message))
    yield put(saveBeforeLoginApartmentInfoFail(error.message))
    yield call(
      reject,
      new SubmissionError({ _error: i18n.t('apartment.actions.formFail') })
    )
  }
}
export function* handleSignUpBeforeLogin({
  payload: { reject, resolve }
}: any): SagaIterator {
  try {
    const beforeLoginApartmentState:
      | BeforeLoginApartment
      | undefined = yield select(selectBeforeLoginApartment)
    if (
      !beforeLoginApartmentState ||
      (beforeLoginApartmentState && !beforeLoginApartmentState.information)
    ) {
      throw new Error('Apartment or apartment info missing from state')
    }
    const {
      metadata,
      information,
      confirmation,
      credentials
    } = beforeLoginApartmentState

    const isAlreadyConfirmed = metadata.confirmed
    const validatedBeforeLoginApartmentState = {
      ...beforeLoginApartmentState
    }

    delete validatedBeforeLoginApartmentState.credentials
    delete validatedBeforeLoginApartmentState.switchedData

    if (credentials) {
      const { name, email, password, phoneNumber, isSVAMember } = credentials

      const isValidEmail = yield call(validateEmail, email)

      if (!isValidEmail) {
        console.log("invalid email")
        throw new Error('EmailValidationFailed')
      }

      yield call(handleSignupOnApartmentConfirmation, {
        email,
        password,
        phoneNumber,
        isSVAMember,
        name
      })
    }

    const userId = yield select(selectUserId)
    if (!userId) {
      throw new Error('UserId not found')
    }

    const beforeLoginMedia: ImageFileWithObjectURL[] = yield select(
      selectBeforeLoginMedia
    )

    const vuokraoviCollateralFromStorage = localStorage.getItem('vuokraoviCollateral')
    let vuokraoviCollateral
    if (vuokraoviCollateralFromStorage !== null) {
      vuokraoviCollateral = vuokraoviCollateralFromStorage === 'true'
      localStorage.removeItem('vuokraoviCollateral')
    }

    const contactInfoPublicOnPlatformsFromStorage = localStorage.getItem('contactInfoPublicOnPlatforms')
    let contactInfoPublicOnPlatforms
    if (contactInfoPublicOnPlatformsFromStorage !== null) {
      contactInfoPublicOnPlatforms = contactInfoPublicOnPlatformsFromStorage === 'true'
      localStorage.removeItem('contactInfoPublicOnPlatforms')
    }

    const hasMedia = beforeLoginMedia && beforeLoginMedia.length > 0
    const addBeforeLoginApartment: AddBeforeLoginApartment = {
      userId,
      ...validatedBeforeLoginApartmentState,
      information,
      confirmation: {
        ...confirmation,
        ...(vuokraoviCollateralFromStorage !== null && { vuokraoviCollateral }),
        ...(contactInfoPublicOnPlatformsFromStorage !== null && { contactInfoPublicOnPlatforms })
      }
    }

    let apartmentId: string
    const isLoggedIn = yield select(selectUserIsAuthenticated)

    if (isLoggedIn) {
      const createdOnLoginApartment: AddBeforeLoginApartment = {
        ...addBeforeLoginApartment,
        metadata: {
          ...addBeforeLoginApartment.metadata,
          createdBeforeLogin: undefined,
          createdOnLogin: true,
          media: hasMedia
        }
      }
      const result = yield call(
        Api.addAndConfirmBeforeLoginApartment,
        createdOnLoginApartment,
        userId
      )
      apartmentId = result.apartmentId

      if (hasMedia) {
        try {
          const files: any = hasMedia
            ? yield call(imageFilesWithObjectURLToFiles, beforeLoginMedia)
            : []
          const keys = yield all(files.map((file: any) => uploadFile(file, apartmentId)))
          yield call(updateApartmentMediaURLs, apartmentId, keys)
        } catch (error) {
          console.error('Media upload error', error)
        }
      }

      yield put(navigate(`/users/${userId}/apartments/${apartmentId}/overview`))
    } else {
      const result = yield call(
        Api.addAndConfirmBeforeLoginApartment,
        addBeforeLoginApartment
      )
      // console.log('result')
      // console.log(result)
      apartmentId = result.apartment.apartmentId

      if (hasMedia) {
        try {
          const files: any = hasMedia
            ? yield call(imageFilesWithObjectURLToFiles, beforeLoginMedia)
            : []
          const indexedFilesAndMetadatas = getFilesMetadataAndIndex(files)
          const filesToUploadMetadata = Object.values(
            indexedFilesAndMetadatas
          ).map(({ metadata }) => metadata)
          addBeforeLoginApartment.filesToUploadMetadata =
            filesToUploadMetadata.length > 0 ? filesToUploadMetadata : undefined
          const { signedS3UploadURLs } = yield call(
            Api.addAndConfirmBeforeLoginApartment,
            addBeforeLoginApartment
          )
          if (signedS3UploadURLs && signedS3UploadURLs.length > 0) {
            yield call(
              uploadMediaUsingSignedS3URLs,
              indexedFilesAndMetadatas,
              signedS3UploadURLs
            )
          }
        } catch (error) {
          console.error('Media upload error', error)
        }
      }
    }
    // Remove background AI improvements; generation is now user-triggered in UI
    // don't send GTM data for admin actions
    if (!isAlreadyConfirmed) {
      addStepCompletion(
        StepCompletionEventNames.listingCreation,
        ListingCreationSteps.submittedListing
      )
    }
    if (isLoggedIn) {
      const message = hasMedia
        ? i18n.t('apartment.confirmation.success')
        : i18n.t('apartment.confirmation.successNoMedia')

      toast.info(message, { autoClose: 15000 })
    } else {
      yield put(navigate(`/`))
      yield put(openDialog('BeforeLoginApartmentSuccess'))
    }
    yield put(signUpBeforeLoginSuccess())
    resolve()
  } catch (error) {
    const errorMessage = getBeforeLoginConfirmErrorMessage(error.message)
    yield call(reject, new SubmissionError({ _error: errorMessage }))
    yield put(confirmBeforeLoginApartmentFail(error.message))
  }
}
export function* handleAddBeforeLoginApartmentMedia({
  payload: {
    formData: { files },
    resolve,
    reject
  }
}: any): SagaIterator {
  try {
    const beforeLoginMediaState: ImageFileWithObjectURL[] = yield select(
      selectBeforeLoginMedia
    )
    const beforeLoginMedia = [...beforeLoginMediaState, ...files]
    validateHasTooManyImages(beforeLoginMedia)
    yield put(addBeforeLoginApartmentMediaSuccess(beforeLoginMedia))
    addFormSubmit('addApartmentMedia')
    resolve()
  } catch (error) {
    yield put(addBeforeLoginApartmentMediaFail(error.message))
    yield call(
      reject,
      new SubmissionError({
        _error:
          error.message === 'tooMany'
            ? i18n.t('error.mediaUpload.tooMany')
            : i18n.t('error.mediaUpload.formFail')
      })
    )
  }
}

export function* handleDeleteBeforeLoginApartmentMedia({
  payload: keyOfMediaToDelete
}: any): SagaIterator {
  try {
    const beforeLoginMediaState: ImageFileWithObjectURL[] = yield select(
      selectBeforeLoginMedia
    )

    const beforeLoginMedia = beforeLoginMediaState.filter(
      (mediaFile) => mediaFile.key !== keyOfMediaToDelete
    )
    if (beforeLoginMediaState.length === beforeLoginMedia.length) {
      throw new Error('Could not find media to delete by key')
    }
    URL.revokeObjectURL(keyOfMediaToDelete)
    yield put(deleteBeforeLoginApartmentMediaSuccess(beforeLoginMedia))
  } catch (error) {
    yield put(deleteBeforeLoginApartmentMediaFail(error.message))
  }
}

export function* handleReorderBeforeLoginApartmentMedia({
  payload: { oldIndex, newIndex }
}: {
  payload: { oldIndex: number; newIndex: number }
}): SagaIterator {
  try {
    const beforeLoginMediaState: ImageFileWithObjectURL[] = yield select(
      selectBeforeLoginMedia
    )
    const beforeLoginMedia = arrayMove(
      beforeLoginMediaState,
      oldIndex,
      newIndex
    )
    yield put(reorderBeforeLoginApartmentMediaSuccess(beforeLoginMedia))
  } catch (error) {
    yield put(reorderBeforeLoginApartmentMediaFail(error.message))
  }
}

export function* handleConfirmBeforeLoginApartmentMedia({
  payload: { reject, resolve }
}: any): SagaIterator {
  try {
    const beforeLoginApartmentState:
      | BeforeLoginApartment
      | undefined = yield select(selectBeforeLoginApartment)
    if (
      !beforeLoginApartmentState ||
      (beforeLoginApartmentState && !beforeLoginApartmentState.information)
    ) {
      throw new Error('Apartment or apartment info missing from state')
    }
    const beforeLoginApartment = {
      ...beforeLoginApartmentState,
      metadata: {
        ...beforeLoginApartmentState.metadata,
        media: true
      },
      updatedAt: getDateNow()
    }
    yield put(confirmBeforeLoginApartmentMediaSuccess(beforeLoginApartment))
    // AFTER ADDING MEDIA GET BEFORELOGIN STATE AGAIN
    const beforeLoginApartmentNewState:
      | BeforeLoginApartment
      | undefined = yield select(selectBeforeLoginApartment)
    if (
      !beforeLoginApartmentNewState ||
      (beforeLoginApartmentNewState &&
        !beforeLoginApartmentNewState.information)
    ) {
      throw new Error('Apartment or apartment info missing from state')
    }
    const {
      metadata,
      information,
      confirmation,
      credentials
    } = beforeLoginApartmentNewState

    const isAlreadyConfirmed = metadata.confirmed
    const validatedBeforeLoginApartmentState = {
      ...beforeLoginApartmentNewState
    }
    delete validatedBeforeLoginApartmentState.credentials
    delete validatedBeforeLoginApartmentState.switchedData
    if (credentials) {
      const { name, email, password, phoneNumber, isSVAMember } = credentials
      yield call(handleSignupOnApartmentConfirmation, {
        email,
        password,
        phoneNumber,
        isSVAMember,
        name
      })
    }

    const userId = yield select(selectUserId)
    if (!userId) {
      throw new Error('UserId not found')
    }
    const beforeLoginMedia: ImageFileWithObjectURL[] = yield select(
      selectBeforeLoginMedia
    )
    const hasMedia = beforeLoginMedia && beforeLoginMedia.length > 0
    const addBeforeLoginApartment: AddBeforeLoginApartment = {
      userId,
      ...validatedBeforeLoginApartmentState,
      information,
      confirmation
    }

    const isLoggedIn = yield select(selectUserIsAuthenticated)
    if (isLoggedIn) {
      const createdOnLoginApartment: AddBeforeLoginApartment = {
        ...addBeforeLoginApartment,
        metadata: {
          ...addBeforeLoginApartment.metadata,
          createdBeforeLogin: undefined,
          createdOnLogin: true,
          media: hasMedia
        }
      }
      const loggedInUserId = userId
      const { apartmentId } = yield call(
        Api.addAndConfirmBeforeLoginApartment,
        createdOnLoginApartment,
        loggedInUserId
      )
      if (hasMedia) {
        try {
          const files: any = hasMedia
            ? yield call(imageFilesWithObjectURLToFiles, beforeLoginMedia)
            : []
          const keys = yield all(files.map((file: any) => uploadFile(file, apartmentId)))
          yield call(updateApartmentMediaURLs, apartmentId, keys);
        } catch (error) {
          /** do not break if error happens when uploading media */
        }
      }
      yield put(
        navigate(`/users/${loggedInUserId}/apartments/${apartmentId}/overview`)
      )
    } else {
      try {
        const files: any = hasMedia
          ? yield call(imageFilesWithObjectURLToFiles, beforeLoginMedia)
          : []
        const indexedFilesAndMetadatas = getFilesMetadataAndIndex(files)
        const filesToUploadMetadata = Object.values(
          indexedFilesAndMetadatas
        ).map(({ metadata }) => metadata)
        addBeforeLoginApartment.filesToUploadMetadata =
          filesToUploadMetadata.length > 0 ? filesToUploadMetadata : undefined
        const { signedS3UploadURLs } = yield call(
          Api.addAndConfirmBeforeLoginApartment,
          addBeforeLoginApartment
        )
        if (signedS3UploadURLs && signedS3UploadURLs.length > 0) {
          yield call(
            uploadMediaUsingSignedS3URLs,
            indexedFilesAndMetadatas,
            signedS3UploadURLs
          )
        }
      } catch (error) {
        /** do not break if error happens when uploading media */
      }
    }

    // don't send GTM data for admin actions
    if (!isAlreadyConfirmed) {
      addStepCompletion(
        StepCompletionEventNames.listingCreation,
        ListingCreationSteps.submittedListing
      )
    }
    if (isLoggedIn) {
      const message = hasMedia
        ? i18n.t('apartment.confirmation.success')
        : i18n.t('apartment.confirmation.successNoMedia')

      toast.info(message, { autoClose: 15000 })
    } else {
      yield put(navigate(`/`))
      yield put(openDialog('BeforeLoginApartmentSuccess'))
    }
    yield put(signUpBeforeLoginSuccess())
    addFormSubmit('confirmApartmentMedia')
    resolve()
  } catch (error) {
    const errorMessage = getBeforeLoginConfirmErrorMessage(error.message)
    yield call(reject, new SubmissionError({ _error: errorMessage }))
    yield put(confirmBeforeLoginApartmentMediaFail(error.message))
  }
}

export function* uploadMediaUsingSignedS3URLs(
  indexedFilesAndMetadatas: IndexedFilesAndMetadata,
  preSignedS3URLResponses: PreSignedS3URLResponse[]
): SagaIterator {
  try {
    let uploadPromises: Promise<any>[] = []
    preSignedS3URLResponses.forEach(({ id, S3PresignedURL }) => {
      if (indexedFilesAndMetadatas[id]?.file) {
        uploadPromises = [
          ...uploadPromises,
          uploadFileUsingPreSignedURL(
            S3PresignedURL,
            indexedFilesAndMetadatas[id].file
          )
        ]
      }
    })

    yield call(promiseAllHelper, uploadPromises)
  } catch (error) {
    throw new Error('Failed to upload media using signed keys')
  }
}

/** More errors might show up later */
const getBeforeLoginConfirmErrorMessage = (
  errorMessage: string | undefined
) => {
  switch (errorMessage) {
    case 'noConfirmOrInformation':
      return i18n.t('apartment.confirmation.confirmFalse')
    case 'NotAuthorizedException':
      return i18n.t('error.beforeLoginApartment.NotAuthorizedException')
    case 'DuplicateNumberException':
      return i18n.t('validation.duplicateUserError')
    case 'EmailValidationFailed':
      return i18n.t('validation.emailValidationError')
    case 'UserNotConfirmedException':
      return (
        i18n.t(`login.${errorMessage}`) +
        '\n\n' +
        i18n.t('error.beforeLoginApartment.confirmEmailFirst')
      )
    case 'Missing validated confirmation form':
    default:
      return i18n.t('apartment.actions.formFail')
  }
}

export function* handleConfirmBeforeLoginApartment({
  payload: { formData, resolve, reject }
}: any): SagaIterator {
  try {
    const beforeLoginApartmentState:
      | BeforeLoginApartment
      | undefined = yield select(selectBeforeLoginApartment)
    if (
      !beforeLoginApartmentState ||
      (beforeLoginApartmentState && !beforeLoginApartmentState.information)
    ) {
      throw new Error('Apartment or apartment info missing from state')
    }

    const {
      information,
      switchedData,
      ...reqBeforeLoginApartmentState
    } = beforeLoginApartmentState

    // if (isAlreadyConfirmed) {
    //   // when apartment is already confirmed, do not try to confirm it again
    //   yield put(navigate(`/users/${userId}/apartments/${apartmentId}/overview`))
    //   return
    // }
    const confirmationRaw: BeforeLoginApartmentConfirmationForm = {
      ...formData,
      ...switchedData
    }
    if (!confirmationRaw.confirm || !information) {
      throw new Error('noConfirmOrInformation')
    }
    const parkingFeeNeeded = showParking(information.parking)

    const unvalidatedConfirmation = deleteUnneededConfirmData(
      confirmationRaw,
      parkingFeeNeeded,
      'beforeLogin'
    )
    const { email, password, phoneNumber, isSVAMember = false, name } = formData
    const confirmation: ApartmentConfirmation = yield call(
      Api.validateApartmentForm,
      'confirmation',
      unvalidatedConfirmation
    )
    if (!confirmation) {
      throw new Error('validationFailed')
    }
    const beforeLoginApartment: BeforeLoginApartment = {
      ...reqBeforeLoginApartmentState,
      confirmation,
      information,
      credentials: { email, password, phoneNumber, isSVAMember, name },
      updatedAt: getDateNow()
    }
    //yield put(navigate(`/create/apartment/steps/11`))
    yield put(confirmBeforeLoginApartmentSuccess(beforeLoginApartment))
    addFormSubmit('apartmentContractDetails')
    addStepCompletion(
      StepCompletionEventNames.listingCreation,
      ListingCreationSteps.filledListingDetails
    )
    yield call(handleSignUpBeforeLogin, { payload: { resolve, reject } });
    resolve()
  } catch (error) {
    yield put(confirmBeforeLoginApartmentFail(error.message))
    const errorMessage = getBeforeLoginConfirmErrorMessage(error.message)
    yield call(reject, new SubmissionError({ _error: errorMessage }))
  }
}

export function* handleAddMissingClientInfo({
  payload: { formData, resolve, reject }
}: HandleUpdateApartmentClientParameters): SagaIterator {
  try {
    const userId = yield select(selectUserId)
    if (!hasOtherOwnerDataIfNeeded(formData)) {
      throw new Error('otherOwnerNotDefined')
    }
    const streetAddress =
      typeof formData?.mainOwner?.autocompleteLocation === 'string'
        ? formData?.mainOwner?.autocompleteLocation
        : formData?.mainOwner?.autocompleteLocation?.label

    const client: ApartmentClientInformation = deleteUnneededClientInfo({
      ...formData
    })
    // sms api format is no plus, countrycode and phone (without leading zero)
    client.mainOwner = {
      ...client.mainOwner,
      streetAddress,
      address: generateApartmentAddress({
        streetAddress,
        buildingLetter: formData.mainOwner.buildingLetter,
        apartmentNumber: formData.mainOwner.apartmentNumber
      })
    }
    const apartmentsWithoutClientInfo: Apartment[] = yield select(
      selectApartmentsWithoutClientInfoByUserId(userId)
    )

    const updateClientInfoPromises = apartmentsWithoutClientInfo.map(
      ({ userId, apartmentId }) =>
        Api.updateApartmentClientInformation(userId, apartmentId, client)
    )

    const updatedApartments: Apartment[] = yield call(
      promiseAllHelper,
      updateClientInfoPromises
    )
    yield all(
      updatedApartments.map((updatedApartment) =>
        put(updateApartmentClientInformationSuccess(updatedApartment))
      )
    )

    const apartmentId = formData.apartment.apartmentId;

    yield put(addMissingClientInfoSuccess())
    yield put(navigate(`/users/${userId}/apartments/${apartmentId}/payment`))
    //yield put(closeDialog('AddMissingClientInfo'))
    resolve()
  } catch (error) {
    const errorMessage =
      error.message === 'otherOwnerNotDefined'
        ? i18n.t('error.client.otherOwnerNotDefined')
        : i18n.t('apartment.actions.formFail')
    yield put(addMissingClientInfoFail(errorMessage))
    yield call(reject, new SubmissionError({ _error: errorMessage }))
  }
}

export function* handleAddMissingClientInfoToOverview({
  payload: { formData, resolve, reject }
}: HandleUpdateApartmentClientParameters): SagaIterator {
  try {
    const userId = yield select(selectUserId)
    if (!hasOtherOwnerDataIfNeeded(formData)) {
      throw new Error('otherOwnerNotDefined')
    }
    const streetAddress =
      typeof formData?.mainOwner?.autocompleteLocation === 'string'
        ? formData?.mainOwner?.autocompleteLocation
        : formData?.mainOwner?.autocompleteLocation?.label

    const client: ApartmentClientInformation = deleteUnneededClientInfo({
      ...formData
    })
    // sms api format is no plus, countrycode and phone (without leading zero)
    client.mainOwner = {
      ...client.mainOwner,
      streetAddress,
      address: generateApartmentAddress({
        streetAddress,
        buildingLetter: formData.mainOwner.buildingLetter,
        apartmentNumber: formData.mainOwner.apartmentNumber
      })
    }
    const apartmentsWithoutClientInfo: Apartment[] = yield select(
      selectApartmentsWithoutClientInfoByUserId(userId)
    )

    const updateClientInfoPromises = apartmentsWithoutClientInfo.map(
      ({ userId, apartmentId }) =>
        Api.updateApartmentClientInformation(userId, apartmentId, client)
    )

    const updatedApartments: Apartment[] = yield call(
      promiseAllHelper,
      updateClientInfoPromises
    )
    yield all(
      updatedApartments.map((updatedApartment) =>
        put(updateApartmentClientInformationSuccess(updatedApartment))
      )
    )

    yield put(addMissingClientInfoToOverviewSuccess())
    //yield put(navigate(`/users/${userId}/apartments/${apartmentId}/payment`))
    yield put(closeDialog('AddMissingClientInfo'))
    yield put(navigate(`/users/${userId}/apartments/${updatedApartments[updatedApartments.length - 1].apartmentId}/overview`))
    const overviewMessage = i18n.t('apartment.overview.firstTimeMessage')
    toast.info(overviewMessage, { autoClose: 30000 });
    resolve()
  } catch (error) {
    const errorMessage =
      error.message === 'otherOwnerNotDefined'
        ? i18n.t('error.client.otherOwnerNotDefined')
        : i18n.t('apartment.actions.formFail')
    yield put(addMissingClientInfoToOverviewFail(errorMessage))
    yield call(reject, new SubmissionError({ _error: errorMessage }))
  }
}
